import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Bug,
  Columns3,
  Lightbulb,
  MessageCircle,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Send,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  api,
  type BoardTicket,
  type KanbanColumn,
  type TicketCommentWithUser,
  type TicketPriority,
  type TicketType,
} from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kanban',
  component: KanbanPage,
})

// ── Helpers ──────────────────────────────────────────────────────────────

const TYPE_META: Record<TicketType, { icon: typeof Bug; label: string; color: string }> = {
  bug: { icon: Bug, label: 'Bug', color: 'text-red-400' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-400' },
  improvement: { icon: Lightbulb, label: 'Verbesserung', color: 'text-amber-400' },
}

const PRIORITY_META: Record<TicketPriority, { label: string; badge: string }> = {
  low: { label: 'Niedrig', badge: 'bg-text-tertiary/15 text-text-tertiary' },
  normal: { label: 'Normal', badge: 'bg-blue-500/15 text-blue-400' },
  high: { label: 'Hoch', badge: 'bg-accent/15 text-accent' },
  critical: { label: 'Kritisch', badge: 'bg-red-500/15 text-red-400' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

// ── Drag State (module-level so it persists across renders) ──────────────

interface DragState {
  ticketId: string
  originColumnId: string
  originOrder: number
  /** The floating card element cloned for the drag ghost. */
  ghost: HTMLElement | null
  /** Currently hovered column id. */
  overColumnId: string | null
  /** Index within the hovered column. */
  overIndex: number
  /** Touch mode vs mouse mode. */
  isTouch: boolean
  /** Long-press timer. */
  longPressTimer: ReturnType<typeof setTimeout> | null
  /** Has the drag actually started (for touch: after long-press). */
  active: boolean
}

// ── Main Page ────────────────────────────────────────────────────────────

function KanbanPage() {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const [filters, setFilters] = useState<{
    type?: string
    priority?: string
    assignedTo?: string
    search?: string
  }>({})

  // Search is debounced (300ms) before it lands in `filters` (and thus
  // the board queryKey), so typing doesn't fire one full board fetch per
  // keystroke. searchInput drives the field; the effect syncs it over.
  const [searchInput, setSearchInput] = useState('')
  useEffect(() => {
    const t = setTimeout(() => {
      const v = searchInput.trim()
      setFilters((f) => (f.search === (v || undefined) ? f : { ...f, search: v || undefined }))
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const isAdmin = !!me?.user && me.user.role === 'admin'

  const { data: boardData, isLoading } = useQuery({
    queryKey: ['kanban', 'board', filters],
    queryFn: () => api.getKanbanBoard(filters),
    enabled: isAdmin,
    refetchInterval: 15_000,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: isAdmin,
  })

  const [addColumnOpen, setAddColumnOpen] = useState(false)
  const [editColumn, setEditColumn] = useState<KanbanColumn | null>(null)
  const [detailTicketId, setDetailTicketId] = useState<string | null>(null)

  // Mobile-Shell (<640): Filter-Sheet hinterm Bottom-Slot (bündelt die
  // Desktop-Filterleiste Suche/Typ/Prio/Assignee/Reset), Titel oben.
  const [filterSheet, setFilterSheet] = useState(false)
  useMobilePageTitle('Board')
  const hasActiveFilter = !!(filters.type || filters.priority || filters.assignedTo || searchInput)

  // Drag-drop state
  const [dragTicketId, setDragTicketId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<{ columnId: string; index: number } | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const ghostRef = useRef<HTMLDivElement | null>(null)

  const moveMut = useMutation({
    mutationFn: (data: { ticketId: string; columnId: string; position: number }) =>
      api.moveTicket(data.ticketId, { columnId: data.columnId, position: data.position }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kanban'] }),
  })

  const columns = boardData?.columns ?? []
  const allTickets = boardData?.tickets ?? []
  const users = usersData?.users ?? []

  // Group tickets by column
  const ticketsByColumn = useMemo(() => {
    const map: Record<string, BoardTicket[]> = {}
    for (const col of columns) {
      map[col.id] = []
    }
    for (const t of allTickets) {
      if (!map[t.columnId]) map[t.columnId] = []
      map[t.columnId].push(t)
    }
    // Sort each column by columnOrder
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => a.columnOrder - b.columnOrder)
    }
    return map
  }, [columns, allTickets])

  // ── Drag handlers ────────────────────────────────────────────────────

  const startDrag = useCallback(
    (ticketId: string, originColumnId: string, originOrder: number, isTouch: boolean) => {
      dragRef.current = {
        ticketId,
        originColumnId,
        originOrder,
        ghost: null,
        overColumnId: null,
        overIndex: 0,
        isTouch,
        longPressTimer: null,
        active: true,
      }
      setDragTicketId(ticketId)
    },
    [],
  )

  const endDrag = useCallback(() => {
    const ds = dragRef.current
    if (ds?.longPressTimer) clearTimeout(ds.longPressTimer)
    if (ds?.active && ds.overColumnId) {
      moveMut.mutate({
        ticketId: ds.ticketId,
        columnId: ds.overColumnId,
        position: ds.overIndex,
      })
    }
    if (ghostRef.current) {
      ghostRef.current.remove()
      ghostRef.current = null
    }
    dragRef.current = null
    setDragTicketId(null)
    setDropTarget(null)
  }, [moveMut])

  const cancelDrag = useCallback(() => {
    const ds = dragRef.current
    if (ds?.longPressTimer) clearTimeout(ds.longPressTimer)
    if (ghostRef.current) {
      ghostRef.current.remove()
      ghostRef.current = null
    }
    dragRef.current = null
    setDragTicketId(null)
    setDropTarget(null)
  }, [])

  // ── Mouse drag (HTML drag API) ─────────────────────────────────────

  const onCardDragStart = useCallback(
    (e: React.DragEvent, ticket: BoardTicket) => {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', ticket.id)
      startDrag(ticket.id, ticket.columnId, ticket.columnOrder, false)
    },
    [startDrag],
  )

  const onColumnDragOver = useCallback((e: React.DragEvent, columnId: string, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragRef.current) {
      dragRef.current.overColumnId = columnId
      dragRef.current.overIndex = index
    }
    // dragover fires continuously (even with a still mouse). Return the
    // previous object identity when column+index are unchanged so React
    // skips re-rendering the whole board on every event.
    setDropTarget((prev) =>
      prev && prev.columnId === columnId && prev.index === index ? prev : { columnId, index },
    )
  }, [])

  const onColumnDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      endDrag()
    },
    [endDrag],
  )

  const onCardDragEnd = useCallback(() => {
    // If drop didn't happen, cancel
    if (dragRef.current) cancelDrag()
  }, [cancelDrag])

  // ── Touch drag (long-press) ──────────────────────────────────────

  const onCardPointerDown = useCallback(
    (e: ReactPointerEvent, ticket: BoardTicket) => {
      if (e.pointerType !== 'touch') return
      // Start a long-press timer
      const timer = setTimeout(() => {
        startDrag(ticket.id, ticket.columnId, ticket.columnOrder, true)
        // Vibrate for haptic feedback (if supported)
        if (navigator.vibrate) navigator.vibrate(40)
      }, 400)
      dragRef.current = {
        ticketId: ticket.id,
        originColumnId: ticket.columnId,
        originOrder: ticket.columnOrder,
        ghost: null,
        overColumnId: null,
        overIndex: 0,
        isTouch: true,
        longPressTimer: timer,
        active: false,
      }
    },
    [startDrag],
  )

  const onCardPointerMove = useCallback((e: ReactPointerEvent) => {
    const ds = dragRef.current
    if (!ds) return
    // If not active yet (long-press not triggered), cancel if finger moved too far
    if (!ds.active) {
      // Cancel the long-press timer if the user moved
      if (ds.longPressTimer) {
        clearTimeout(ds.longPressTimer)
        ds.longPressTimer = null
      }
      dragRef.current = null
      return
    }
    // Move ghost to pointer position
    if (ghostRef.current) {
      ghostRef.current.style.left = `${e.clientX - 80}px`
      ghostRef.current.style.top = `${e.clientY - 30}px`
    }
    // Hit-test columns
    const el = document.elementFromPoint(e.clientX, e.clientY)
    if (el) {
      const colEl = el.closest('[data-column-id]')
      if (colEl) {
        const colId = colEl.getAttribute('data-column-id') ?? ''
        const cardEls = colEl.querySelectorAll('[data-card-index]')
        let idx = 0
        for (const card of cardEls) {
          const rect = card.getBoundingClientRect()
          if (e.clientY > rect.top + rect.height / 2) {
            idx = Number(card.getAttribute('data-card-index')) + 1
          }
        }
        ds.overColumnId = colId
        ds.overIndex = idx
        setDropTarget({ columnId: colId, index: idx })
      }
    }
  }, [])

  const onCardPointerUp = useCallback(() => {
    const ds = dragRef.current
    if (!ds) return
    if (ds.active) {
      endDrag()
    } else {
      cancelDrag()
    }
  }, [endDrag, cancelDrag])

  // Column management mutations
  const deleteCol = useMutation({
    mutationFn: (id: string) => api.deleteKanbanColumn(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kanban'] })
      notify.success('Spalte gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />
  if (!isAdmin) return <Navigate to="/tickets" />

  return (
    // <640 füllt h-full den Page-Container (in __root auf Scroller-Höhe
    // geklemmt) exakt zwischen den Shell-Bars. sm: = Tablet-Band 640–1023
    // (T2, Topbar gelöscht, Bottom-Bar h-14): alter md-Wert 6rem + Bar
    // 3.5rem (56px) = 9.5rem, plus Bar-Safe-Area-Inset (Standalone;
    // Browser-Tab env() = 0). lg: stellt den Desktop-Wert (6rem, vorher
    // md:) wieder her — ≥1024 byte-identisch. phone:h-full: im Handy-
    // Querformat (Breite ≥640, Shell-Bars aktiv) gewännen sonst die
    // sm:/lg:-dvh-Klemmen gegen die Shell-Scroller-Höhe.
    <section className="animate-fade-in flex flex-col h-full min-h-0 sm:h-[calc(100dvh-9.5rem-var(--app-safe-bottom))] lg:h-[calc(100dvh-6rem)] phone:h-full">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Filter → Sheet + ＋ Neue Spalte —
          Handler identisch zu den Desktop-Controls. */}
      <MobileBottomSlots>
        <BarSlot
          label="Filter"
          icon={<SlidersHorizontal className="h-5 w-5" />}
          active={hasActiveFilter}
          ariaLabel="Board filtern"
          ariaHasPopup
          onClick={() => setFilterSheet(true)}
        />
        <BarAccentPlus ariaLabel="Neue Spalte" onClick={() => setAddColumnOpen(true)} />
      </MobileBottomSlots>

      {/* Filter-Sheet (<640): dieselben States wie die Desktop-Filterleiste —
          searchInput bleibt debounced (300ms) Richtung Board-Query. */}
      <SidePanel open={filterSheet} onClose={() => setFilterSheet(false)} title="Filter">
        <div className="space-y-5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Suchen…"
              className="pl-8"
            />
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Typ</p>
            <div className="flex flex-col gap-1">
              <SheetRow
                active={!filters.type}
                label="Alle Typen"
                onClick={() => setFilters((f) => ({ ...f, type: undefined }))}
              />
              {(Object.entries(TYPE_META) as [TicketType, (typeof TYPE_META)[TicketType]][]).map(
                ([k, v]) => (
                  <SheetRow
                    key={k}
                    active={filters.type === k}
                    label={v.label}
                    onClick={() => setFilters((f) => ({ ...f, type: k }))}
                  />
                ),
              )}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Priorität</p>
            <div className="flex flex-col gap-1">
              <SheetRow
                active={!filters.priority}
                label="Alle Prioritäten"
                onClick={() => setFilters((f) => ({ ...f, priority: undefined }))}
              />
              {(
                Object.entries(PRIORITY_META) as [
                  TicketPriority,
                  (typeof PRIORITY_META)[TicketPriority],
                ][]
              ).map(([k, v]) => (
                <SheetRow
                  key={k}
                  active={filters.priority === k}
                  label={v.label}
                  onClick={() => setFilters((f) => ({ ...f, priority: k }))}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs uppercase tracking-wider text-text-tertiary">Bearbeiter</p>
            <div className="flex flex-col gap-1">
              <SheetRow
                active={!filters.assignedTo}
                label="Alle Bearbeiter"
                onClick={() => setFilters((f) => ({ ...f, assignedTo: undefined }))}
              />
              {users.map((u) => (
                <SheetRow
                  key={u.id}
                  active={filters.assignedTo === u.id}
                  label={u.name}
                  onClick={() => setFilters((f) => ({ ...f, assignedTo: u.id }))}
                />
              ))}
            </div>
          </div>
          {hasActiveFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters({})
                setSearchInput('')
              }}
            >
              <X className="h-3.5 w-3.5" /> Zurücksetzen
            </Button>
          )}
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Board"
        actions={
          <Button onClick={() => setAddColumnOpen(true)} size="sm">
            <Plus className="h-4 w-4" /> Spalte
          </Button>
        }
      />

      {/* Filter bar — <640 übernimmt das Filter-Sheet der Bottom-Bar diese
          Controls (phone:hidden); ab 640 unverändert einzeilig.
          py-1 statt Primitive-py-2: bei h-8 blieben sonst nur 14px für die
          16px-Zeile → vertikal geclippte Oberlängen. */}
      <div className="flex flex-wrap items-center gap-2 mb-3 phone:hidden">
        <Input
          placeholder="Suchen…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full h-8 py-1 text-xs sm:w-40"
        />
        <Select
          value={filters.type ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value || undefined }))}
          className="w-full h-8 py-1 text-xs truncate sm:w-32"
        >
          <option value="">Alle Typen</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="improvement">Verbesserung</option>
        </Select>
        <Select
          value={filters.priority ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value || undefined }))}
          className="w-full h-8 py-1 text-xs truncate sm:w-32"
        >
          <option value="">Alle Prioritäten</option>
          <option value="low">Niedrig</option>
          <option value="normal">Normal</option>
          <option value="high">Hoch</option>
          <option value="critical">Kritisch</option>
        </Select>
        <Select
          value={filters.assignedTo ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, assignedTo: e.target.value || undefined }))}
          className="w-full h-8 py-1 text-xs truncate sm:w-40"
        >
          <option value="">Alle Bearbeiter</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        {(filters.type || filters.priority || filters.assignedTo || searchInput) && (
          <div className="col-span-2 sm:col-span-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters({})
                setSearchInput('')
              }}
            >
              <X className="h-3.5 w-3.5" /> Zurücksetzen
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-text-tertiary py-8 text-center">Lade Board …</p>
      ) : columns.length === 0 ? (
        <EmptyState
          icon={Columns3}
          message="Keine Spalten angelegt."
          action={
            <Button size="sm" onClick={() => setAddColumnOpen(true)}>
              <Plus className="h-4 w-4" /> Neue Spalte
            </Button>
          }
        />
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden min-h-0">
          <div className="flex gap-3 h-full pb-2" style={{ minWidth: `${columns.length * 290}px` }}>
            {columns.map((col) => {
              const colTickets = ticketsByColumn[col.id] ?? []
              return (
                <KanbanColumnView
                  key={col.id}
                  column={col}
                  tickets={colTickets}
                  dragTicketId={dragTicketId}
                  dropTarget={dropTarget}
                  onEditColumn={() => setEditColumn(col)}
                  onDeleteColumn={() => {
                    if (col.isDefault) {
                      notify.error('Standard-Spalte kann nicht gelöscht werden')
                      return
                    }
                    if (
                      confirm(
                        `Spalte „${col.name}“ löschen? Tickets werden nach Eingang verschoben.`,
                      )
                    )
                      deleteCol.mutate(col.id)
                  }}
                  onCardClick={(t) => setDetailTicketId(t.id)}
                  onCardDragStart={onCardDragStart}
                  onCardDragEnd={onCardDragEnd}
                  onColumnDragOver={onColumnDragOver}
                  onColumnDrop={onColumnDrop}
                  onCardPointerDown={onCardPointerDown}
                  onCardPointerMove={onCardPointerMove}
                  onCardPointerUp={onCardPointerUp}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Touch drag ghost */}
      {dragTicketId &&
        dragRef.current?.isTouch &&
        dragRef.current.active &&
        (() => {
          const draggedTicket = allTickets.find((t) => t.id === dragTicketId)
          return draggedTicket ? <TouchGhost ticket={draggedTicket} ghostRef={ghostRef} /> : null
        })()}

      {/* Side panels */}
      {addColumnOpen && (
        <AddColumnPanel
          onClose={() => setAddColumnOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['kanban'] })
            setAddColumnOpen(false)
          }}
        />
      )}
      {editColumn && (
        <EditColumnPanel
          column={editColumn}
          onClose={() => setEditColumn(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['kanban'] })
            setEditColumn(null)
          }}
        />
      )}
      {detailTicketId && (
        <KanbanTicketDetail
          ticketId={detailTicketId}
          myId={me.user.id}
          isAdmin={isAdmin}
          users={users}
          onClose={() => setDetailTicketId(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['kanban'] })}
        />
      )}
    </section>
  )
}

// ── Column View ──────────────────────────────────────────────────────────

function KanbanColumnView({
  column,
  tickets,
  dragTicketId,
  dropTarget,
  onEditColumn,
  onDeleteColumn,
  onCardClick,
  onCardDragStart,
  onCardDragEnd,
  onColumnDragOver,
  onColumnDrop,
  onCardPointerDown,
  onCardPointerMove,
  onCardPointerUp,
}: {
  column: KanbanColumn
  tickets: BoardTicket[]
  dragTicketId: string | null
  dropTarget: { columnId: string; index: number } | null
  onEditColumn: () => void
  onDeleteColumn: () => void
  onCardClick: (t: BoardTicket) => void
  onCardDragStart: (e: React.DragEvent, t: BoardTicket) => void
  onCardDragEnd: () => void
  onColumnDragOver: (e: React.DragEvent, columnId: string, index: number) => void
  onColumnDrop: (e: React.DragEvent) => void
  onCardPointerDown: (e: ReactPointerEvent, t: BoardTicket) => void
  onCardPointerMove: (e: ReactPointerEvent) => void
  onCardPointerUp: () => void
}) {
  const isDropTarget = dropTarget?.columnId === column.id

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop target
    <div
      data-column-id={column.id}
      className={cn(
        'w-[270px] shrink-0 flex flex-col rounded-2xl border bg-bg-elevated/40 overflow-hidden transition-colors',
        isDropTarget ? 'border-accent/50 bg-accent/5' : 'border-border-subtle',
      )}
      onDragOver={(e) => {
        e.preventDefault()
        // Calculate drop index from mouse position
        const cardEls = e.currentTarget.querySelectorAll('[data-card-index]')
        let idx = 0
        for (const card of cardEls) {
          const rect = card.getBoundingClientRect()
          if (e.clientY > rect.top + rect.height / 2) {
            idx = Number(card.getAttribute('data-card-index')) + 1
          }
        }
        onColumnDragOver(e, column.id, idx)
      }}
      onDrop={onColumnDrop}
    >
      {/* Column header */}
      <header className="px-3 py-2.5 border-b border-border-subtle flex items-center gap-2 bg-bg-overlay/30">
        {column.color && (
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: column.color }}
          />
        )}
        <span className="text-xs font-medium text-text-primary flex-1 truncate">{column.name}</span>
        <span className="text-2xs tabular-nums text-text-tertiary bg-bg-overlay rounded-full px-1.5 py-0.5">
          {tickets.length}
        </span>
        <button
          type="button"
          onClick={onEditColumn}
          aria-label="Spalte bearbeiten"
          title="Spalte bearbeiten"
          className="p-1 text-text-tertiary hover:text-text-primary touch:opacity-100"
        >
          <Pencil className="h-3 w-3" />
        </button>
        {!column.isDefault && (
          <button
            type="button"
            onClick={onDeleteColumn}
            aria-label="Spalte löschen"
            title="Spalte löschen"
            className="p-1 text-text-tertiary hover:text-red-400 touch:opacity-100"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </header>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60px]">
        {tickets.length === 0 && (
          // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop target
          <div
            className="h-16 rounded-lg border-2 border-dashed border-border-subtle flex items-center justify-center"
            onDragOver={(e) => onColumnDragOver(e, column.id, 0)}
          >
            <span className="text-2xs text-text-tertiary">Leer</span>
          </div>
        )}
        {tickets.map((t, idx) => {
          const isDragging = dragTicketId === t.id
          const showDropIndicator = isDropTarget && dropTarget?.index === idx

          return (
            <div key={t.id}>
              {showDropIndicator && (
                <div className="h-0.5 bg-accent rounded-full mb-1 animate-fade-in" />
              )}
              <KanbanCard
                ticket={t}
                index={idx}
                isDragging={isDragging}
                onClick={() => onCardClick(t)}
                onDragStart={(e) => onCardDragStart(e, t)}
                onDragEnd={onCardDragEnd}
                onPointerDown={(e) => onCardPointerDown(e, t)}
                onPointerMove={onCardPointerMove}
                onPointerUp={onCardPointerUp}
              />
            </div>
          )
        })}
        {/* Show drop indicator at end */}
        {isDropTarget && dropTarget?.index === tickets.length && (
          <div className="h-0.5 bg-accent rounded-full animate-fade-in" />
        )}
      </div>
    </div>
  )
}

// ── Card ─────────────────────────────────────────────────────────────────

function KanbanCard({
  ticket,
  index,
  isDragging,
  onClick,
  onDragStart,
  onDragEnd,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: {
  ticket: BoardTicket
  index: number
  isDragging: boolean
  onClick: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragEnd: () => void
  onPointerDown: (e: ReactPointerEvent) => void
  onPointerMove: (e: ReactPointerEvent) => void
  onPointerUp: () => void
}) {
  const tm = TYPE_META[ticket.type as TicketType] ?? TYPE_META.bug
  const pm = PRIORITY_META[ticket.priority as TicketPriority] ?? PRIORITY_META.normal
  const Icon = tm.icon

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: card click
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop card
    <div
      data-card-index={index}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={onClick}
      className={cn(
        'rounded-lg border border-border-subtle bg-bg-elevated p-2.5 cursor-grab active:cursor-grabbing',
        'hover:border-border-default/80 transition-all touch:opacity-100 select-none',
        isDragging && 'opacity-30 scale-95',
      )}
      style={{ touchAction: 'none' }}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <Icon className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', tm.color)} strokeWidth={1.8} />
        <p className="text-xs font-medium text-text-primary leading-snug flex-1 line-clamp-2">
          {ticket.title}
        </p>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={cn('text-2xs px-1 py-0.5 rounded font-medium leading-none', pm.badge)}>
          {pm.label}
        </span>
        {ticket.creatorName && (
          <span className="text-2xs text-text-tertiary truncate max-w-[80px]">
            {ticket.creatorName}
          </span>
        )}
        <span className="flex-1" />
        {ticket.attachmentCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-2xs text-text-tertiary">
            <Paperclip className="h-2.5 w-2.5" />
            {ticket.attachmentCount}
          </span>
        )}
        {ticket.commentCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-2xs text-text-tertiary">
            <MessageCircle className="h-2.5 w-2.5" />
            {ticket.commentCount}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Touch Ghost ──────────────────────────────────────────────────────────

function TouchGhost({
  ticket,
  ghostRef,
}: {
  ticket: BoardTicket
  ghostRef: React.MutableRefObject<HTMLDivElement | null>
}) {
  const tm = TYPE_META[ticket.type as TicketType] ?? TYPE_META.bug
  const Icon = tm.icon

  return createPortal(
    <div ref={ghostRef} className="fixed z-[9999] pointer-events-none" style={{ left: 0, top: 0 }}>
      <div className="w-[200px] rounded-lg border border-accent/60 bg-bg-elevated p-2.5 shadow-xl opacity-90">
        <div className="flex items-start gap-2">
          <Icon className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', tm.color)} strokeWidth={1.8} />
          <p className="text-xs font-medium text-text-primary leading-snug line-clamp-2">
            {ticket.title}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── Add Column Panel ─────────────────────────────────────────────────────

function AddColumnPanel({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [color, setColor] = useState('#6366F1')

  const create = useMutation({
    mutationFn: () => api.createKanbanColumn({ name, color }),
    onSuccess: () => {
      notify.success('Spalte erstellt')
      onCreated()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Neue Spalte"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            Erstellen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="col-name">Name</Label>
          <Input
            id="col-name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 100))}
            placeholder="z.B. Backlog"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="col-color">Farbe</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              id="col-color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-8 rounded border border-border-default cursor-pointer"
            />
            <span className="text-xs text-text-tertiary">{color}</span>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

// ── Edit Column Panel ────────────────────────────────────────────────────

function EditColumnPanel({
  column,
  onClose,
  onSaved,
}: {
  column: KanbanColumn
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(column.name)
  const [color, setColor] = useState(column.color ?? '#6366F1')

  const update = useMutation({
    mutationFn: () => api.updateKanbanColumn(column.id, { name, color }),
    onSuccess: () => {
      notify.success('Spalte aktualisiert')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title={`Spalte: ${column.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button disabled={!name.trim() || update.isPending} onClick={() => update.mutate()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ec-name">Name</Label>
          <Input
            id="ec-name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 100))}
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ec-color">Farbe</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              id="ec-color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-8 w-8 rounded border border-border-default cursor-pointer"
            />
            <span className="text-xs text-text-tertiary">{color}</span>
          </div>
        </div>
      </div>
    </SidePanel>
  )
}

// ── Kanban Ticket Detail ─────────────────────────────────────────────────

function KanbanTicketDetail({
  ticketId,
  myId,
  isAdmin,
  users,
  onClose,
  onChanged,
}: {
  ticketId: string
  myId: string
  isAdmin: boolean
  users: Array<{ id: string; name: string; email: string }>
  onClose: () => void
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.getTicket(ticketId),
    refetchInterval: 15_000,
  })

  const updateMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateTicket(ticketId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
      onChanged()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: () => api.deleteTicket(ticketId),
    onSuccess: () => {
      notify.success('Ticket gelöscht')
      onChanged()
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (isLoading || !data) {
    return (
      <SidePanel open onClose={onClose} title="Ticket">
        <p className="text-sm text-text-tertiary py-4 text-center">Lade …</p>
      </SidePanel>
    )
  }

  const { ticket, attachments, comments, creatorName } = data
  const tm = TYPE_META[ticket.type as TicketType] ?? TYPE_META.bug
  const pm = PRIORITY_META[ticket.priority as TicketPriority] ?? PRIORITY_META.normal

  return (
    <SidePanel
      open
      onClose={onClose}
      title={ticket.title}
      description={`${tm.label} · ${pm.label} · ${formatDate(ticket.createdAt)}`}
      footer={
        <Button
          variant="ghost"
          className="text-red-400 hover:text-red-300"
          onClick={() => {
            if (confirm('Ticket wirklich löschen?')) deleteMut.mutate()
          }}
        >
          <Trash2 className="h-4 w-4" /> Löschen
        </Button>
      }
    >
      <div className="space-y-4 pb-2">
        {/* Admin controls */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-2xs">Typ</Label>
            <Select
              value={ticket.type}
              onChange={(e) => updateMut.mutate({ type: e.target.value })}
              className="h-8 text-xs"
            >
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
              <option value="improvement">Verbesserung</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-2xs">Priorität</Label>
            <Select
              value={ticket.priority}
              onChange={(e) => updateMut.mutate({ priority: e.target.value })}
              className="h-8 text-xs"
            >
              <option value="low">Niedrig</option>
              <option value="normal">Normal</option>
              <option value="high">Hoch</option>
              <option value="critical">Kritisch</option>
            </Select>
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-2xs">Zugewiesen an</Label>
          <Select
            value={ticket.assignedTo ?? ''}
            onChange={(e) => updateMut.mutate({ assignedTo: e.target.value || null })}
            className="h-8 text-xs"
          >
            <option value="">Nicht zugewiesen</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </Select>
        </div>

        {/* Creator */}
        <div className="text-xs text-text-tertiary">
          Erstellt von:{' '}
          <span className="text-text-secondary">{creatorName ?? ticket.createdBy}</span>
        </div>

        {/* Description */}
        <div className="text-sm text-text-primary whitespace-pre-wrap break-words bg-bg-overlay/30 rounded-lg p-3 border border-border-subtle">
          {ticket.description}
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
              Anhänge ({attachments.length})
            </p>
            <ul className="space-y-1">
              {attachments.map((att) => (
                <li key={att.id} className="flex items-center gap-2 text-xs">
                  <Paperclip className="h-3 w-3 text-text-tertiary shrink-0" />
                  <a
                    href={api.ticketAttachmentUrl(ticketId, att.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline truncate flex-1"
                  >
                    {att.filename}
                  </a>
                  <span className="text-text-tertiary tabular-nums shrink-0">
                    {(att.sizeBytes / 1024).toFixed(0)} KB
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comments */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
            Kommentare ({comments.length})
          </p>
          {comments.length === 0 ? (
            <p className="text-xs text-text-tertiary">Noch keine Kommentare.</p>
          ) : (
            <ul className="space-y-2">
              {comments.map((c) => (
                <KanbanCommentItem
                  key={c.id}
                  comment={c}
                  canDelete={c.userId === myId || isAdmin}
                  ticketId={ticketId}
                  onChanged={onChanged}
                />
              ))}
            </ul>
          )}
          <KanbanAddComment ticketId={ticketId} onChanged={onChanged} />
        </div>
      </div>
    </SidePanel>
  )
}

function KanbanCommentItem({
  comment,
  canDelete,
  ticketId,
  onChanged,
}: {
  comment: TicketCommentWithUser
  canDelete: boolean
  ticketId: string
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const del = useMutation({
    mutationFn: () => api.deleteTicketComment(ticketId, comment.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
      onChanged()
    },
  })

  return (
    <div className="bg-bg-overlay/40 rounded-lg p-2.5 border border-border-subtle">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-text-secondary">
          {comment.userName ?? 'Unbekannt'}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-2xs text-text-tertiary tabular-nums">
            {formatDate(comment.createdAt)}
          </span>
          {canDelete && (
            <button
              type="button"
              onClick={() => del.mutate()}
              className="p-0.5 text-text-tertiary hover:text-red-400 touch:opacity-100"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-text-primary whitespace-pre-wrap break-words">{comment.content}</p>
    </div>
  )
}

function KanbanAddComment({ ticketId, onChanged }: { ticketId: string; onChanged: () => void }) {
  const qc = useQueryClient()
  const [content, setContent] = useState('')
  const add = useMutation({
    mutationFn: () => api.addTicketComment(ticketId, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
      setContent('')
      onChanged()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!content.trim() || add.isPending) return
    add.mutate()
  }

  return (
    <form onSubmit={onSubmit} className="flex items-end gap-2 mt-2">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value.slice(0, 5000))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onSubmit(e as unknown as FormEvent)
          }
        }}
        rows={2}
        placeholder="Kommentar schreiben …"
        className="flex-1 rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-none"
      />
      <Button type="submit" disabled={!content.trim() || add.isPending} size="sm">
        <Send className="h-3.5 w-3.5" />
      </Button>
    </form>
  )
}
